<template>
  <el-dialog
    v-model="dialogVisible"
    modal-class="dept-picker-overlay"
    :title="multiple ? `选择部门（已选 ${selectedIds.length}）` : '选择部门'"
    width="460px"
    class="dept-picker-dialog"
    append-to-body
    destroy-on-close
  >
    <div class="dept-picker-body">
      <div class="dept-search">
        <el-input
          v-model="searchKeyword"
          class="dept-search-input"
          placeholder="搜索部门名称"
          clearable
          @keyup.enter="handleSearch"
          @clear="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <button type="button" class="dept-search-button is-primary" @click="handleSearch">搜索</button>
        <button type="button" class="dept-search-button" @click="handleReset">重置</button>
      </div>

      <div class="dept-tree-panel" v-loading="loading">
        <el-tree
          v-if="deptTree.length > 0"
          ref="treeRef"
          class="dept-tree"
          :data="deptTree"
          :props="{ label: 'name', children: 'children', isLeaf: 'leaf' }"
          :default-expanded-keys="expandedKeys"
          node-key="id"
          show-checkbox
          :default-checked-keys="selectedIds"
          :filter-node-method="filterNode"
          @check="handleCheck"
        >
          <template #default="{ node }">
            <div class="dept-node">
              <span class="dept-node-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 00-2 0v1H8a1 1 0 000 2h1v1a1 1 0 002 0v-1h1a1 1 0 000-2h-1V9z" clip-rule="evenodd" />
                </svg>
              </span>
              <span class="dept-node-label">{{ node.label }}</span>
            </div>
          </template>
        </el-tree>
        <el-empty v-else-if="!loading" description="暂无部门数据" :image-size="64" />
      </div>
    </div>

    <template #footer>
      <div class="dept-picker-footer">
        <span class="dept-footer-info">
          已选择 <em>{{ selectedIds.length }}</em> 个部门
        </span>
        <div class="dept-footer-actions">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleConfirm">确定</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { Search } from '@element-plus/icons-vue'
import { getDeptTree } from '@/api/dept'

const props = defineProps({
  modelValue: {
    type: [Array, String, Number],
    default: () => []
  },
  multiple: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = ref(false)
const loading = ref(false)
const searchKeyword = ref('')
const deptTree = ref([])
const treeRef = ref()
const expandedKeys = ref([])

const selectedIds = computed(() => {
  if (!props.modelValue) return []
  const arr = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
  return arr.filter(item => item != null).map(item => (typeof item === 'object' ? item.id : item))
})

const loadDeptTree = async () => {
  try {
    loading.value = true
    const res = await getDeptTree()
    const processTree = (nodes) => {
      return nodes.map(node => ({
        ...node,
        leaf: !node.children || node.children.length === 0,
        children: node.children ? processTree(node.children) : []
      }))
    }
    deptTree.value = processTree(res || [])
    expandedKeys.value = deptTree.value.map(d => d.id)
  } catch (error) {
    console.error('加载部门树失败:', error)
  } finally {
    loading.value = false
  }
}

const filterNode = (value, nodeData) => {
  if (!value) return true
  return nodeData.name?.includes(value) || false
}

const handleSearch = () => {
  if (treeRef.value) {
    treeRef.value.filter(searchKeyword.value)
  }
}

const handleReset = () => {
  searchKeyword.value = ''
  handleSearch()
}

const handleCheck = () => {
  if (treeRef.value) {
    const checkedNodes = treeRef.value.getCheckedNodes()
    const values = checkedNodes.map(node => ({ id: node.id, name: node.name }))
    emit('update:modelValue', props.multiple ? values : values[0] || null)
  }
}

const handleConfirm = () => {
  if (treeRef.value) {
    const checkedNodes = treeRef.value.getCheckedNodes()
    const values = checkedNodes.map(node => ({ id: node.id, name: node.name }))
    emit('update:modelValue', props.multiple ? values : values[0] || null)
  }
  dialogVisible.value = false
}

watch(dialogVisible, (val) => {
  if (val) {
    loadDeptTree()
  }
})

const open = () => {
  dialogVisible.value = true
}

defineExpose({ open })
</script>

<style scoped lang="scss">
:global(.dept-picker-overlay) {
  position: fixed !important;
  inset: 0 !important;
}

:global(.dept-picker-overlay .el-overlay-dialog) {
  position: fixed !important;
  inset: 0 !important;
  display: grid !important;
  place-items: center !important;
  padding: 16px;
  overflow: auto;
  box-sizing: border-box;
}

:global(.dept-picker-dialog) {
  position: relative !important;
  top: auto !important;
  right: auto !important;
  left: auto !important;
  width: min(460px, calc(100vw - 32px)) !important;
  max-height: calc(100vh - 32px);
  margin: 0 !important;
  overflow: hidden;
  border-radius: 14px !important;
  transform: none !important;
}

:global(.dept-picker-dialog .el-dialog__header) {
  padding: 16px 20px 10px !important;
  margin-right: 0 !important;
  border-bottom: 1px solid #e2e8f0;
}

:global(.dept-picker-dialog .el-dialog__title) {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

:global(.dept-picker-dialog .el-dialog__body) {
  padding: 14px 16px 12px !important;
}

:global(.dept-picker-dialog .el-dialog__footer) {
  padding: 0 !important;
  border-top: 0 !important;
}

.dept-picker-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dept-search {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 8px;
  align-items: center;
  padding: 10px;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background-color: #f8fbff;
}

.dept-search-input {
  :deep(.el-input__wrapper) {
    min-height: 34px !important;
    border-radius: 8px !important;
    box-shadow: 0 0 0 1px #cbd5e1 inset !important;
    background-color: #ffffff !important;
    padding-left: 10px !important;
  }

  :deep(.el-input__wrapper.is-focus) {
    box-shadow: var(--form-control-focus-ring, 0 0 0 3px rgba(37, 99, 235, 0.14)), 0 0 0 1px var(--form-control-focus, #2563eb) inset !important;
  }

  :deep(.el-input__inner) {
    height: 32px !important;
    color: #0f172a;
    font-size: 13px;
    line-height: 32px !important;
  }

  :deep(.el-input__inner::placeholder) {
    color: #94a3b8;
  }

  :deep(.el-input__prefix) {
    color: #94a3b8;
    margin-right: 4px;
  }
}

.dept-search-button {
  min-width: 64px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background-color: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    border-color: #94a3b8;
    background-color: #f8fafc;
    color: #334155;
  }

  &.is-primary {
    border-color: #2563eb;
    background-color: #2563eb;
    color: #ffffff;

    &:hover {
      border-color: #1d4ed8;
      background-color: #1d4ed8;
      color: #ffffff;
      box-shadow: 0 6px 14px rgba(37, 99, 235, 0.22);
    }
  }
}

.dept-tree-panel {
  height: 280px;
  padding: 6px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background-color: #ffffff;
}

.dept-tree {
  --el-tree-node-hover-bg-color: #f8fafc;
  --el-tree-text-color: #334155;

  :deep(.el-tree-node__content) {
    min-height: 36px;
    height: auto;
    margin: 1px 0;
    padding-right: 8px;
    border: 1px solid transparent;
    border-radius: 8px;
    transition: background-color 0.16s ease, border-color 0.16s ease;
  }

  :deep(.el-tree-node__content:hover) {
    border-color: #e2e8f0;
    background-color: #f8fafc;
  }

  :deep(.el-tree-node.is-checked > .el-tree-node__content) {
    border-color: #bfdbfe;
    background-color: #eff6ff;
  }

  :deep(.el-tree-node__expand-icon) {
    color: #94a3b8;
  }

  :deep(.el-checkbox) {
    height: 16px;
    margin-right: 8px;
  }

  :deep(.el-checkbox__inner) {
    width: 16px;
    height: 16px;
    border-color: #cbd5e1;
    border-radius: 4px;
  }

  :deep(.el-checkbox__input.is-checked .el-checkbox__inner),
  :deep(.el-checkbox__input.is-indeterminate .el-checkbox__inner) {
    border-color: #2563eb;
    background-color: #2563eb;
  }
}

.dept-node {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  color: #334155;
}

.dept-node-icon {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  background-color: #dcfce7;
  color: #16a34a;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12px;
    height: 12px;
  }
}

.dept-node-label {
  min-width: 0;
  overflow: hidden;
  color: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dept-picker-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.dept-footer-info {
  font-size: 13px;
  color: #64748b;

  em {
    margin: 0 2px;
    color: #2563eb;
    font-style: normal;
    font-weight: 600;
  }
}

.dept-footer-actions {
  display: flex;
  gap: 8px;
}

:global(.dept-picker-dialog .dept-footer-actions .el-button) {
  min-width: 72px;
  height: 34px;
  border-radius: 8px;
  font-weight: 500;
}

:global(.dept-picker-dialog .dept-footer-actions .el-button--primary) {
  background-color: #2563eb;
  border-color: #2563eb;
}
</style>
